import { useContext, useEffect, useState } from 'react';
import {
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  MenuProps,
  Tooltip,
} from '@mui/material';
import DownloadOptionsList from './DownloadOptionsList';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import errorResponses from '../../../errorResponses';
import useGlynt from '../../hooks/useGlynt';
import useGetPackager from '../../hooks/packager/useGetPackager';
import { AppContext } from '../../../App';
import { TemplateFileFormat, TemplateReportType } from '../../../types/packager-types';
import { saveAs } from 'file-saver';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';

type DownloadFormat = 'csv' | 'json';

interface DownloadButtonProps {
  extractionBatchId: string;
  extractionBatchLabel: string;
  trainingSetId?: string;
  failed: boolean;
}

const MenuStyled = styled(Menu)<MenuProps>(() => ({
  '& .MuiPaper-root': {
    border: '1px solid #d3d4d5',
  },
}));

const MenuItemStyled = styled(MenuItem)({
  '&:focus': {
    backgroundColor: 'transparent',
  },
});

const DownloadButton: React.FC<DownloadButtonProps> = ({
  extractionBatchId,
  extractionBatchLabel,
  trainingSetId,
  failed,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [disabled, setDisabled] = useState(false);
  const [downloadParams, setDownloadParams] = useState<{
    format: DownloadFormat;
    option: string;
  } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportType, setReportType] = useState('');
  const [selectedCsvOption, setSelectedCsvOption] = useState<string | null>(null);
  const [selectedJsonOption, setSelectedJsonOption] = useState<string | null>(null);
  const [selectedTemplateOption, setSelectedTemplateOption] = useState<string | null>(null);
  const { dataPoolId, isStaff, setToast } = useContext(AppContext);
  const glynt = useGlynt();
  const { packager } = useGetPackager(dataPoolId);
  const csvOptions = ['transformed', 'wide', 'stats', isStaff ? 'long' : null];
  const jsonOptions = ['transformed', 'default'];

  // Packager activity templates, applied to this batch's transformed rows.
  const templates = packager?.custom_output_config?.templates ?? {};
  const templateOptions = Object.keys(templates).filter(
    (name) =>
      (templates[name].report_type ?? TemplateReportType.ACTIVITY) === TemplateReportType.ACTIVITY
  );

  const { refetch: refetchDistributionCsv } = useQuery({
    queryKey: ['distributionReport'],

    queryFn: async () => {
      return await (
        await glynt
      ).extractionBatch.distribution(extractionBatchId, {
        advanced: true,
        format: 'csv',
        report_type: reportType,
        training_sets: trainingSetId,
      });
    },

    enabled: false,
  });

  const { refetch: refetchDistributionJson } = useQuery({
    queryKey: ['distributionReport'],

    queryFn: async () =>
      await (
        await glynt
      ).extractionBatch.distribution(extractionBatchId, {
        advanced: true,
        training_sets: trainingSetId,
      }),

    enabled: false,
  });

  const { refetch: refetchTransformCsv } = useQuery({
    queryKey: ['transformerCsv', extractionBatchId],

    queryFn: async () =>
      await (
        await glynt
      ).extractionBatch.transformer(extractionBatchId, {
        format: 'csv',
        training_sets: trainingSetId,
      }),

    enabled: false,
  });

  const { refetch: refetchTransformJson } = useQuery({
    queryKey: ['transformerJson', extractionBatchId],

    queryFn: async () =>
      await (
        await glynt
      ).extractionBatch.transformer(extractionBatchId, {
        format: 'json',
        training_sets: trainingSetId,
      }),

    enabled: false,
  });

  useEffect(() => {
    // Only call downloadReport if downloadParams is set
    if (downloadParams) {
      downloadReport(downloadParams.format, downloadParams.option);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadParams]);

  const downloadReport = async (format: DownloadFormat, option: string) => {
    setDisabled(true);
    setToast({
      variant: 'success',
      open: true,
      message: `Downloading ${format.toUpperCase()}`,
    });

    let refetchFunc;
    // Choose the appropriate refetch function based on the format and selected option.
    if (option === 'transformed') {
      refetchFunc = format === 'csv' ? () => refetchTransformCsv() : () => refetchTransformJson();
    } else {
      refetchFunc =
        format === 'csv' ? () => refetchDistributionCsv() : () => refetchDistributionJson();
    }

    try {
      let result = await refetchFunc();
      if (result.isSuccess && result.data) {
        // Construct the blob and filename for download.
        const blobType =
          format === 'csv' ? 'application/csv;charset=utf-8' : 'application/json;charset=utf-8';
        let filename;

        if (option === 'transformed') {
          filename = trainingSetId
            ? `${extractionBatchLabel}-${trainingSetId}-extraction-transformed-${extractionBatchId}.${format}`
            : `${extractionBatchLabel}-extraction-transformed-${extractionBatchId}.${format}`;
        } else {
          filename = trainingSetId
            ? `${extractionBatchLabel}-${trainingSetId}-extraction-${option}-report-${extractionBatchId}.${format}`
            : `${extractionBatchLabel}-extraction-${option}-report-${extractionBatchId}.${format}`;
        }

        const responseData = format === 'csv' ? result.data : JSON.stringify(result.data);
        const blob = new Blob([responseData as BlobPart], {
          type: blobType,
        });
        saveAs(blob, filename);
        setToast({ open: false });
      } else {
        handleDownloadError(result.error, format);
      }
    } catch (error) {
      handleDownloadError(error, format);
    } finally {
      setDisabled(false);
    }
  };

  // Downloads the batch through one packager template, in the template's file format.
  const downloadTemplate = async (template: string) => {
    const format: DownloadFormat =
      templates[template].file_format === TemplateFileFormat.JSON ? 'json' : 'csv';
    setSelectedTemplateOption(template);
    setSelectedCsvOption(null);
    setSelectedJsonOption(null);
    setDisabled(true);
    setToast({
      variant: 'success',
      open: true,
      message: `Downloading ${template}`,
    });

    try {
      const extractionBatch = (await glynt).extractionBatch;
      const responseData =
        format === 'csv'
          ? await extractionBatch.transformer(extractionBatchId, {
              format: 'csv',
              training_sets: trainingSetId,
              template,
            })
          : JSON.stringify(
              await extractionBatch.transformer(extractionBatchId, {
                format: 'json',
                training_sets: trainingSetId,
                template,
              })
            );
      const blobType =
        format === 'csv' ? 'application/csv;charset=utf-8' : 'application/json;charset=utf-8';
      const filename = trainingSetId
        ? `${extractionBatchLabel}-${trainingSetId}-${template}-${extractionBatchId}.${format}`
        : `${extractionBatchLabel}-${template}-${extractionBatchId}.${format}`;
      saveAs(new Blob([responseData], { type: blobType }), filename);
      setToast({ open: false });
    } catch (error) {
      handleDownloadError(error, format);
    } finally {
      setDisabled(false);
    }
  };

  // Handles any errors that might occur during the report download.
  // Displays an error message in the application's context.
  const handleDownloadError = (error: any, format: any) => {
    let message = `An error occurred while retrieving ${format.toUpperCase()} data. Please try again or contact your Glynt representative.`;
    if (
      error instanceof Error &&
      error.message.includes('The batch contains transformations that were not found')
    ) {
      message = errorResponses.transformationNotFound;
    }
    setToast({
      variant: 'error',
      open: true,
      message,
    });
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleDownload = (format: DownloadFormat, option: string) => {
    setReportType(option);
    setDownloadParams({ format, option });

    setSelectedCsvOption(format === 'csv' ? option : null);
    setSelectedJsonOption(format === 'json' ? option : null);
    setSelectedTemplateOption(null);
  };

  return (
    <div>
      <Tooltip title={failed ? 'Extraction failed, please re extract' : ''}>
        <span>
          <IconButton
            onClick={handleDownloadClick}
            data-cy={`btn-download-${extractionBatchLabel}`}
            disabled={failed}
          >
            {disabled ? (
              <CircularProgress size={20} style={{ transform: 'translateY(3px)' }} />
            ) : (
              <GetAppRoundedIcon style={{ transform: 'translateY(3px)' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <MenuStyled
        id="menu-download-wrapper"
        data-cy="menu-download-wrapper"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleDownloadClose}
      >
        <MenuItemStyled style={{ background: 'transparent' }}>
          <DownloadOptionsList
            failed={disabled || failed}
            listTitle="CSV"
            listOptions={csvOptions}
            onSelectOption={(option: string) => handleDownload('csv', option)}
            selectedOption={selectedCsvOption}
            setSelectedOption={setSelectedCsvOption}
          />
        </MenuItemStyled>
        <Divider />
        <MenuItemStyled style={{ background: 'transparent' }}>
          <DownloadOptionsList
            failed={disabled || failed}
            listTitle="JSON"
            listOptions={jsonOptions}
            onSelectOption={(option: string) => handleDownload('json', option)}
            selectedOption={selectedJsonOption}
            setSelectedOption={setSelectedJsonOption}
          />
        </MenuItemStyled>
        {templateOptions.length > 0 && <Divider />}
        {templateOptions.length > 0 && (
          <MenuItemStyled style={{ background: 'transparent' }}>
            <DownloadOptionsList
              failed={disabled || failed}
              listTitle="Packager templates"
              listOptions={templateOptions}
              onSelectOption={downloadTemplate}
              selectedOption={selectedTemplateOption}
              setSelectedOption={setSelectedTemplateOption}
            />
          </MenuItemStyled>
        )}
      </MenuStyled>
    </div>
  );
};

export default DownloadButton;
